import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { textContrastIsAA } from 'helpers/getContrast'

import { ChatIcon } from '@pleio/icons/chat'
import { ChatFillIcon } from '@pleio/icons/chat-fill'

import Popover from 'js/components/Popover'
import NavigationAction from 'js/layout/Navigation/components/NavigationAction'
import NavigationBadge from 'js/layout/Navigation/components/NavigationBadge'

import NavigationChatView from './NavigationChatView'

const NavigationChat = ({ user }) => {
    const popoverActionsRef = useRef(null)

    const { t } = useTranslation()

    const [visible, setVisible] = useState(false)

    const { data, refetch, subscribeToMore } = useQuery<any>(QUERY, {
        fetchPolicy: 'cache-and-network',
    })

    useEffect(() => {
        const unsubscribe = subscribeToMore({
            document: ON_CHATS_UPDATE,
            updateQuery: (prev, { subscriptionData }) => {
                if (!subscriptionData.data) {
                    return prev
                }

                const { action } = subscriptionData.data.onChatsUpdate

                if (action === 'refetch') {
                    refetch()
                }

                return prev
            },
        })
        return () => unsubscribe()
    }, [subscribeToMore, refetch])

    const handleHide = () => {
        popoverActionsRef?.current?.close()
    }

    const totalUnread = data?.chats?.totalUnread
    const hasUnread = !!totalUnread && totalUnread !== 0

    return (
        <Popover
            ariaLabel={t('chat.title')}
            actionsRef={popoverActionsRef}
            open={visible}
            setOpen={setVisible}
            content={
                <NavigationChatView
                    visible={visible}
                    onHide={handleHide}
                    user={user}
                />
            }
            sideOffset={0}
            positionIsFixed={true}
        >
            <NavigationAction
                tooltip={t('chat.title')}
                aria-label={t('chat.open-chat-window')}
            >
                {textContrastIsAA(
                    getComputedStyle(document.documentElement)
                        .getPropertyValue('--ref-palette-header-main')
                        .trim(),
                ) ? (
                    <ChatFillIcon />
                ) : (
                    <ChatIcon />
                )}
                {hasUnread && (
                    <NavigationBadge>
                        {totalUnread < 100 ? totalUnread : '99+'}
                    </NavigationBadge>
                )}
            </NavigationAction>
        </Popover>
    )
}

const QUERY = gql`
    query UnreadChats {
        chats {
            totalUnread
            total
        }
    }
`

const ON_CHATS_UPDATE = gql`
    subscription onChatsUpdate {
        onChatsUpdate {
            guid
            action
        }
    }
`

export default NavigationChat
